import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './index.css';

// Components
import ScrollToTop from './components/ScrollToTop';
import MobileAppBanner from './components/MobileAppBanner';
import MaintenanceWrapper from './components/MaintenanceWrapper';

// Pages
import HomePage from './pages/HomePage';
import HomePageV2 from './pages/HomePageV2';
import UploadPage from './pages/UploadPage';
import LoginPage from './pages/LoginPage';
import MobileLoginPage from './pages/MobileLoginPage';
import LoadingPage from './pages/LoadingPage';
import AuthCallback from './pages/AuthCallback';
import NotFoundPage from './pages/NotFoundPage';
import AccountSettingsPage from './pages/AccountSettingsPage.tsx';
import AllActivityPage from './pages/AllActivityPage';
import CSVEditorPage from './pages/CSVEditorPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import AffiliatePage from './pages/AffiliatePage';
import AffiliateInfoPage from './pages/AffiliateInfoPage';
import RedeemVoucherPage from './pages/RedeemVoucherPage.tsx';
import MobileAppPage from './pages/MobileAppPage';
import MobileAppReferralPage from './pages/MobileAppReferralPage';
import GettingStartedPage from './pages/GettingStartedPage';

// Upload V2 Page
import UploadV2Page from './pages/UploadV2Page';

// 🚀 CODE SPLITTING: Lazy load non-critical pages for faster initial load
const ResultPage = lazy(() => import('./pages/ResultPage'));
const ResultPageV2 = lazy(() => import('./pages/ResultPageV2'));
const AccountRestrictedPage = lazy(() => import('./pages/AccountRestrictedPage'));
const MyUploadsPage = lazy(() => import('./pages/MyUploadsPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ApiDocumentationPage = lazy(() => import('./pages/public').then(module => ({ default: module.ApiDocumentationPage })));
const TermsOfServicePage = lazy(() => import('./pages/public').then(module => ({ default: module.TermsOfServicePage })));
const PrivacyPolicyPage = lazy(() => import('./pages/public').then(module => ({ default: module.PrivacyPolicyPage })));
const DesktopPage = lazy(() => import('./pages/public').then(module => ({ default: module.DesktopPage })));
const ChangelogPage = lazy(() => import('./pages/public/ChangelogPage'));
const FeaturesPage = lazy(() => import('./pages/public/FeaturesPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const BlogManagementPage = lazy(() => import('./admin/pages/BlogManagementPage'));
// Auth Components - BULLETPROOF SYSTEM
import { ProtectedRoute, GuestOnlyRoute } from './components/auth/AuthGuard';

// Admin Module - Clean separation
import { 
  AdminLoginPage, 
  AdminDashboardPage,
  UsersPage,
  TransactionsPage,
  MetadataKeywordsPage,
  AdminVoucherManagementPage,
  AdminProtectedRoute, 
  AdminGuestOnlyRoute 
} from './admin';
import AIAnalyticsPage from './admin/pages/AIAnalyticsPage';
import AdminAffiliateManagementPage from './admin/pages/AdminAffiliateManagementPage';
import SiteSettingsPage from './admin/pages/SiteSettingsPage';
import PushNotificationsPage from './admin/pages/PushNotificationsPage';

// Contexts
import { AuthModalProvider } from './contexts/AuthModalContext';

// Stores
import { useAuthStore } from './stores/authStore';

// Hooks
import { useReferralTracking } from './hooks/useReferralTracking';

// Auth Bridge for integration
// AuthBridge removed - using simplified auth flow

function App() {
  const { initialize } = useAuthStore();
  
  // Track referral clicks across all pages
  useReferralTracking();

  // Initialize auth state on app startup with bridge integration
  useEffect(() => {
    // Initialize auth system
    initialize().catch(error => {
      console.error('Failed to initialize auth system:', error);
    });
  }, [initialize]);

  return (
    <MaintenanceWrapper>
      <AuthModalProvider>
            <ScrollToTop />
            <Toaster />
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
              <Routes>
            {/* Home Page - Accessible to all users (authenticated and guest) */}
            <Route path="/" element={<HomePageV2 />} />
            
            {/* Pricing Page - Accessible to all users */}
            <Route path="/pricing" element={<PricingPage />} />
            
            {/* API Documentation Page - Accessible to all users */}
            <Route path="/api-docs" element={<ApiDocumentationPage />} />

            {/* Changelog Page - Accessible to all users */}
            <Route path="/changelog" element={<ChangelogPage />} />

            {/* Features Page - Accessible to all users */}
            <Route path="/features" element={<FeaturesPage />} />
            
            {/* Desktop App Page - Accessible to all users */}
            <Route path="/desktop" element={<DesktopPage />} />

            {/* Terms of Service Page - Accessible to all users */}
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />

            {/* Privacy Policy Page - Accessible to all users */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            
            {/* Contact Us Page - Accessible to all users */}
            <Route path="/contact" element={<ContactPage />} />
            
            {/* Blog Pages - Accessible to all users */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            
            {/* Affiliate Info Page - Public */}
            <Route path="/affiliate-program" element={<AffiliateInfoPage />} />
            
            {/* Mobile App Page - Public */}
            <Route path="/mobile-app" element={<MobileAppPage />} />
            
            {/* Mobile App Referral Landing - Public */}
            <Route path="/app" element={<MobileAppReferralPage />} />
            
            {/* Getting Started - Video Series Page - Public */}
            <Route path="/getting-started" element={<GettingStartedPage />} />
            
            {/* Login Page - Guest only */}
            <Route path="/login" element={
              <GuestOnlyRoute>
                <LoginPage />
              </GuestOnlyRoute>
            } />
            
            {/* Mobile Login Page - Public (for mobile app OAuth) */}
            <Route path="/mobile-login" element={<MobileLoginPage />} />
            
            {/* Loading Page - Public */}
            <Route path="/loading" element={<LoadingPage />} />
            
            {/* Protected Routes */}
            <Route path="/upload" element={
              <ProtectedRoute>
                <UploadPage />
              </ProtectedRoute>
            } />
            
            {/* Upload Routes */}
            <Route path="/upload" element={
              <ProtectedRoute>
                <UploadV2Page />
              </ProtectedRoute>
            } />
            
            <Route path="/my-account" element={
              <ProtectedRoute>
                <AccountSettingsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/my-uploads" element={
              <ProtectedRoute>
                <MyUploadsPage />
              </ProtectedRoute>
            } />

            <Route path="/csv-editor" element={
              <ProtectedRoute>
                <CSVEditorPage />
              </ProtectedRoute>
            } />

            <Route path="/all-activity" element={
              <ProtectedRoute>
                <AllActivityPage />
              </ProtectedRoute>
            } />
            
            <Route path="/affiliate" element={
              <ProtectedRoute>
                <AffiliatePage />
              </ProtectedRoute>
            } />
            
            {/* Redeem Voucher Page - Protected */}
            <Route path="/redeem" element={
              <ProtectedRoute>
                <RedeemVoucherPage />
              </ProtectedRoute>
            } />
            
            {/* OAuth Callback - Public but with special handling */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Account Restricted - Public (for banned/suspended users) */}
            <Route path="/account/restricted" element={<AccountRestrictedPage />} />
            
            {/* Result Page - Public for now (testing) */}
            <Route path="/results/:albumSlug" element={<ResultPage />} />
            
            {/* Result Page V2 - Album Detail & Metadata Editor Only */}
            <Route path="/results-v2/:albumSlug" element={<ResultPageV2 />} />
            
            {/* Admin Routes */}
            <Route path="/manage" element={
              <AdminGuestOnlyRoute>
                <AdminLoginPage />
              </AdminGuestOnlyRoute>
            } />
            <Route path="/manage/*" element={
              <AdminProtectedRoute>
                <AdminDashboardPage />
              </AdminProtectedRoute>
            }>
              {/* Nested Admin Routes FOR LOAD ADMIN PAGES! */}
              <Route path="dashboard" element={<div />} /> {/* Default dashboard content is handled in AdminDashboardPage */}
              <Route path="users" element={<UsersPage />} />
              <Route path="albums" element={<div className="p-6"><h1 className="text-2xl font-bold">Albums Management</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
              <Route path="transactions" element={<TransactionsPage />} />
              <Route path="ai-analytics" element={<AIAnalyticsPage />} />
              <Route path="credits" element={<div className="p-6"><h1 className="text-2xl font-bold">Credits Management</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
              <Route path="affiliates" element={<AdminAffiliateManagementPage />} />
              <Route path="voucher" element={<AdminVoucherManagementPage />} />
              <Route path="metadata" element={<MetadataKeywordsPage />} />
              <Route path="blog" element={<BlogManagementPage />} />
              <Route path="activity" element={<div className="p-6"><h1 className="text-2xl font-bold">Activity Logs</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
              <Route path="settings" element={<SiteSettingsPage />} />
              <Route path="push-notifications" element={<PushNotificationsPage />} />
            </Route>
            
            
            {/* Catch all other routes - 404 */}
            <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>

          {/* Mobile App Download Banner - Shows on Android devices */}
          <MobileAppBanner />
      </AuthModalProvider>
    </MaintenanceWrapper>
  );
}

export default App;